import React, { useEffect, useState } from 'react';
import { useRecordContext, useDataProvider } from 'react-admin';
import { Box, Chip, Divider, Grid, Paper, Stack, Typography, Skeleton } from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import { ImageWithFallback } from '../../../../components';
import { GiftProductType, IntegrationType } from '../../../../models';

export const ProductBox = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [productsMap, setProductsMap] = useState<Map<string, any>>(new Map());
    const [saleProductsMap, setSaleProductsMap] = useState<Map<string, any>>(new Map());

    // Loading flags to show skeletons while fetching related data
    const [productsLoading, setProductsLoading] = useState(false);
    const [saleProductsLoading, setSaleProductsLoading] = useState(false);

    useEffect(() => {
        if (!record || !record.giftProducts) return;

        // Fetch product catalog data for existing products
        const productIds = new Set<string>();
        record.giftProducts.forEach((giftProduct: any) => {
            if (giftProduct.productType === 'existing' && giftProduct.productId) {
                productIds.add(giftProduct.productId);
            }
        });

        if (productIds.size > 0) {
            setProductsLoading(true);
            dataProvider.getMany('chains/chainId/products', {
                ids: Array.from(productIds),
            }).then(({ data }) => {
                const map = new Map<string, any>();
                data.forEach((product: any) => {
                    map.set(product.id, product);
                });
                setProductsMap(map);
            }).catch(error => {
                console.error('Error fetching products:', error);
            }).finally(() => setProductsLoading(false));
        } else {
            setProductsLoading(false);
        }

        // Fetch sale products for virtual products
        const saleProductIds = new Set<string>();
        record.giftProducts.forEach((giftProduct: any) => {
            if (giftProduct.productType === 'virtual' && giftProduct.saleProductIds) {
                giftProduct.saleProductIds.forEach((id: string) => saleProductIds.add(id));
            }
        });

        if (saleProductIds.size > 0) {
            setSaleProductsLoading(true);
            dataProvider.getMany('chains/chainId/sale-products', {
                ids: Array.from(saleProductIds),
            }).then(({ data }) => {
                const map = new Map<string, any>();
                data.forEach((product: any) => {
                    map.set(product.id, product);
                });
                setSaleProductsMap(map);
            }).catch(error => {
                console.error('Error fetching sale products:', error);
            }).finally(() => setSaleProductsLoading(false));
        } else {
            setSaleProductsLoading(false);
        }
    }, [record, dataProvider]);

    const getProductTypeLabel = (type: string, cashValue?: number | null): { label: string; color: string; bgColor: string } => {
        // Recompensa em dinheiro é produto virtual no banco; o rótulo segue o que foi cadastrado.
        if (cashValue != null) {
            return { label: 'Cashback', color: '#2e7d32', bgColor: '#e8f5e9' };
        }

        switch (type) {
            case GiftProductType.Virtual:
                return { label: 'Produto Virtual', color: '#1976d2', bgColor: '#e3f2fd' };
            case GiftProductType.Existing:
                return { label: 'Produto Existente', color: '#7b1fa2', bgColor: '#f3e5f5' };
            case GiftProductType.FuelDiscount:
                return { label: 'Desconto Abastecimento', color: '#f57c00', bgColor: '#fff3e0' };
            case GiftProductType.PredictionTicket:
                return { label: 'Direito ao Palpite (Bolão)', color: '#7869DF', bgColor: '#ede9fe' };
            case GiftProductType.DrawingNumber:
                return { label: 'Número da Sorte (Sorteio)', color: '#00897b', bgColor: '#e0f2f1' };
            case GiftProductType.SpinTicket:
                return { label: 'Giro na Roleta', color: '#7869DF', bgColor: '#ede9fe' };
            default:
                return { label: type, color: '#666', bgColor: '#f5f5f5' };
        }
    };

    const getProductFallbackIcon = (type: string) => {
        switch (type) {
            case GiftProductType.Virtual:
                return <CardGiftcardIcon sx={{ fontSize: 28, color: '#1976d2' }} />;
            case GiftProductType.Existing:
                return <InventoryIcon sx={{ fontSize: 28, color: '#7b1fa2' }} />;
            case GiftProductType.FuelDiscount:
                return <LocalGasStationIcon sx={{ fontSize: 28, color: '#f57c00' }} />;
            case GiftProductType.PredictionTicket:
                return <SportsSoccerIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
            case GiftProductType.DrawingNumber:
                return <CasinoIcon sx={{ fontSize: 28, color: '#00897b' }} />;
            case GiftProductType.SpinTicket:
                return <CasinoIcon sx={{ fontSize: 28, color: '#7869DF' }} />;
            default:
                return <CardGiftcardIcon sx={{ fontSize: 28, color: '#666' }} />;
        }
    };

    const getProductImage = (giftProduct: any): string | null => {
        if (giftProduct.productType === GiftProductType.Virtual || giftProduct.productType === GiftProductType.Existing) {
            return giftProduct.productImagePath || null;
        }
        return null;
    };

    const getProductName = (giftProduct: any): string => {
        if (giftProduct.productType === GiftProductType.Virtual) {
            return giftProduct.productName || 'Sem nome';
        } else if (giftProduct.productType === GiftProductType.Existing) {
            return productsMap.get(giftProduct.productId)?.name || giftProduct.productId || 'Produto do catálogo';
        } else if (giftProduct.productType === GiftProductType.FuelDiscount) {
            return 'Desconto no Combustível';
        } else if (giftProduct.productType === GiftProductType.PredictionTicket) {
            return `Direito ao palpite${giftProduct.bolaoName ? `: ${giftProduct.bolaoName}` : ''}`;
        } else if (giftProduct.productType === GiftProductType.DrawingNumber) {
            return `Número da sorte${giftProduct.drawingName ? `: ${giftProduct.drawingName}` : ''}`;
        } else if (giftProduct.productType === GiftProductType.SpinTicket) {
            return `Giro na roleta${giftProduct.roletaName ? `: ${giftProduct.roletaName}` : ''}`;
        }
        return 'Produto';
    };

    if (!record || !record.giftProducts || record.giftProducts.length === 0) {
        return null;
    }

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <LocalOfferIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>Recompensas</Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                    {record.giftProducts.map((giftProduct: any, index: number) => {
                        const typeInfo = getProductTypeLabel(giftProduct.productType, giftProduct.cashValue);
                        const productNumber = String(index + 1).padStart(2, '0');

                        return (
                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={giftProduct.id || index}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: '#fafafa',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <ImageWithFallback
                                            src={getProductImage(giftProduct)}
                                            alt={getProductName(giftProduct)}
                                            width={60}
                                            height={60}
                                            borderRadius={8}
                                            fallbackIcon={getProductFallbackIcon(giftProduct.productType)}
                                        />
                                        <Box flex={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                                                <Chip
                                                    label={typeInfo.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: typeInfo.bgColor,
                                                        color: typeInfo.color,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                <Chip
                                                    label={`#${productNumber}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#f5f5f5',
                                                        color: '#666',
                                                        fontWeight: 600,
                                                    }}
                                                />
                                                {giftProduct.integrationType && (
                                                    <Chip
                                                        label={IntegrationType.translateType(giftProduct.integrationType as IntegrationType)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: giftProduct.integrationType === IntegrationType.rti ? '#e8f5e9'
                                                                : giftProduct.integrationType === IntegrationType.evo ? '#fff3e0'
                                                                : giftProduct.integrationType === IntegrationType.teixeiraPinto ? '#fce4ec'
                                                                : '#e8eaf6',
                                                            color: giftProduct.integrationType === IntegrationType.rti ? '#2e7d32'
                                                                : giftProduct.integrationType === IntegrationType.evo ? '#e65100'
                                                                : giftProduct.integrationType === IntegrationType.teixeiraPinto ? '#c62828'
                                                                : '#283593',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                            {/* Product name with skeleton for existing products while loading */}
                                            {giftProduct.productType === 'existing' ? (
                                                productsLoading ? (
                                                    <Skeleton variant="text" width={120} animation="wave" sx={{ mb: 0.5 }} />
                                                ) : (
                                                    <Typography variant="body1" fontWeight={600} mb={0.5} sx={{ transition: 'opacity 300ms ease', opacity: productsMap.has(giftProduct.productId) ? 1 : 0 }}>
                                                        {productsMap.get(giftProduct.productId)?.name || 'Produto do catálogo'}
                                                    </Typography>
                                                )
                                            ) : (
                                                <Typography variant="body1" fontWeight={600} mb={0.5}>
                                                    {getProductName(giftProduct)}
                                                </Typography>
                                            )}

                                            {/* Product description skeleton */}
                                            {giftProduct.productType === 'virtual' && giftProduct.productDescription && (
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    {giftProduct.productDescription}
                                                </Typography>
                                            )}
                                            {giftProduct.productType === 'existing' && (
                                                productsLoading ? (
                                                    <Skeleton variant="text" width="80%" animation="wave" />
                                                ) : (
                                                    productsMap.get(giftProduct.productId)?.description && (
                                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                                            {productsMap.get(giftProduct.productId).description}
                                                        </Typography>
                                                    )
                                                )
                                            )}
                                            {giftProduct.productType === 'fuelDiscount' && giftProduct.discountId && (
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    Vinculado a desconto de recompensa
                                                </Typography>
                                            )}
                                            {giftProduct.productType === 'virtual' && giftProduct.saleProductIds && giftProduct.saleProductIds.length > 0 && (
                                                <Box mt={1}>
                                                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                        Produtos de Venda:
                                                    </Typography>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {giftProduct.saleProductIds.map((productId: any, idx: number) => {
                                                            const id = typeof productId === 'object' ? (productId.id || productId.saleProductId) : productId;
                                                            const nameFromObj = typeof productId === 'object' && (productId.name || productId.saleProductName);
                                                            const label = nameFromObj || saleProductsMap.get(id)?.name || id;

                                                            return (saleProductsLoading && !saleProductsMap.has(id)) ? (
                                                                <Skeleton key={idx} variant="rectangular" width={80} height={24} animation="wave" sx={{ borderRadius: 1 }} />
                                                            ) : (
                                                                <Chip
                                                                    key={idx}
                                                                    label={label}
                                                                    size="small"
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                                />
                                                            );
                                                        })} 
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
    );
};
